import {
  AlertCircle,
  Bell,
  CheckCircle2,
  CheckCheck,
  InfoIcon,
  Trash2,
  Shield,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ColoredOutlineButton,
  OUTLINE_ICON_BUTTON_ICON_CLASS,
  OUTLINE_TEXT_BUTTON_ICON_CLASS,
} from "@/components/ui/colored-outline-button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Notification } from "@/types/notification";
import { getNotificationTemplate, resolveNotificationStatus, resolveNotificationTitle } from "./notificationTemplates";
import { useTextColorClass } from "@/components/ui/text-coloring";
import { notificationLabels } from "@/i18n";
import { useLanguage } from "@/context/LanguageContext";
import type { NotificationPageSize } from "@/services/notification/notificationService";
import { Pagination } from "@/components/ui/pagination";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";

interface NotificationListProps {
  notifications: Notification[];
  totalNotifications: number;
  totalPages: number;
  page: number;
  pageSize: number;
  pageSizeOptions: readonly number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: NotificationPageSize) => void;
  onNotificationClick: (notification: Notification) => void;
  onDeleteNotification: (notificationId: number) => void;
  onMarkAsRead: (notificationId: number) => void;
  onMarkAllAsRead: () => void;
  onDeleteAll: () => void;
  isMarkingAllAsRead: boolean;
  isDeletingAll: boolean;
}

export const NotificationList = ({
  notifications,
  totalNotifications,
  totalPages,
  page,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
  onNotificationClick,
  onDeleteNotification,
  onMarkAsRead,
  onMarkAllAsRead,
  onDeleteAll,
  isMarkingAllAsRead,
  isDeletingAll,
}: NotificationListProps) => {
  const { baseText, subtleText, mutedText } = useTextColorClass();
  const { language } = useLanguage();
  const notificationLabels_i18n = notificationLabels(language);
  const { isNative } = useNativePlatform();

  const getStatusColor = (status: "action_required" | "completed" | "info") => {
    switch (status) {
      case "action_required":
        return "bg-orange-500/10 text-orange-700 hover:bg-orange-500/20";
      case "completed":
        return "bg-green-500/10 text-green-500 hover:bg-green-500/20";
      default:
        return "bg-blue-500/10 text-blue-500 hover:bg-blue-500/20";
    }
  };

  const getStatusText = (status: "action_required" | "completed" | "info") => {
    switch (status) {
      case "action_required":
        return notificationLabels_i18n.status?.actionRequired;
      case "completed":
        return notificationLabels_i18n.status?.completed;
      default:
        return notificationLabels_i18n.status?.info;
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    const truncated =
      text.length > maxLength ? text.substring(0, maxLength).trimEnd() : text;
    return text.length > maxLength ? `${truncated}...` : text;
  };

  return (
    <Card
      className={`mb-4 glass-card ${isNative ? "w-full max-w-none border-x-0 rounded-lg" : ""}`}
    >
      <CardHeader className="bg-gradient-to-r from-transparent to-white/5 sm:p-4 p-3">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div className="flex gap-2 items-center">
            <Bell className="h-5 w-5" />
            <h2 className="text-lg font-semibold">
              {notificationLabels_i18n.title}
            </h2>
          </div>

          <div className="flex gap-2 flex-wrap w-full sm:w-auto">
            <ColoredOutlineButton
              type="button"
              variant="blue"
              tintedBackground
              size="sm"
              onClick={onMarkAllAsRead}
              disabled={isMarkingAllAsRead || notifications.length === 0}
              isLoading={isMarkingAllAsRead}
              className="flex-1 sm:flex-initial min-w-[120px] justify-center"
            >
              <CheckCheck
                className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                strokeWidth={2.35}
              />
              <span>
                {isMarkingAllAsRead
                  ? notificationLabels_i18n.marking
                  : notificationLabels_i18n.markAllAsRead}
              </span>
            </ColoredOutlineButton>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <ColoredOutlineButton
                  type="button"
                  variant="calendar"
                  tintedBackground
                  size="sm"
                  disabled={isDeletingAll || notifications.length === 0}
                  isLoading={isDeletingAll}
                  className="flex-1 sm:flex-initial min-w-[120px] justify-center"
                >
                  <Trash2
                    className={OUTLINE_TEXT_BUTTON_ICON_CLASS}
                    strokeWidth={2.35}
                  />
                  <span>
                    {isDeletingAll
                      ? notificationLabels_i18n.deleting
                      : notificationLabels_i18n.deleteAll}
                  </span>
                </ColoredOutlineButton>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{notificationLabels_i18n.deleteConfirmTitle}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {notificationLabels_i18n.deleteConfirmDescription}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{notificationLabels_i18n.cancel}</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={onDeleteAll}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    {notificationLabels_i18n.deleteAll}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-2 sm:p-4">
        <div className="space-y-3">
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {notificationLabels_i18n.noNotifications}
            </div>
          ) : (
            notifications.map((notification) => {
              const template = getNotificationTemplate(notification.type, language);
              if (!template) return null;

              const title = resolveNotificationTitle(
                template,
                notification.data || {},
              );
              const description = template.description(notification.data || {});
              const status = resolveNotificationStatus(
                template,
                notification.data || {},
              );

              return (
                <div
                  key={notification.id}
                  className={`p-3 sm:p-4 rounded-lg hover:bg-white/5 transition-colors cursor-pointer ${
                    !notification.read
                      ? "border-l-4 border-l-[#007AFF] dark:border-l-blue-400 border-t border-r border-b border-gray-200 dark:border-white/20 dark:bg-blue-500/10"
                      : "border border-gray-200 dark:border-white/10 glass-card"
                  }`}
                  onClick={() => {
                    onNotificationClick(notification);
                    if (!notification.read) {
                      onMarkAsRead(notification.id);
                    }
                  }}
                >
                  <div className="flex items-start justify-between gap-2 sm:gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3
                          className={`font-medium text-sm sm:text-base ${baseText}`}
                        >
                          {title}
                        </h3>
                        {notification.isAdminNotification && (
                          <Badge
                            variant="outline"
                            className="text-xs bg-purple-500/10 text-purple-700 border-purple-300 dark:border-purple-600 dark:text-purple-400 dark:bg-purple-500/20"
                          >
                            <Shield className="h-3 w-3 mr-1" />
                            Admin
                          </Badge>
                        )}
                      </div>
                      <p className={`text-xs sm:text-sm whitespace-pre-line ${subtleText}`}>
                        {truncateText(description, 100)}
                      </p>
                      <p className={`text-xs mt-1 sm:mt-2 ${mutedText}`}>
                        {notification.timestamp}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-xs ${getStatusColor(status)}`}>
                        {status === "action_required" && (
                          <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        )}
                        {status === "completed" && (
                          <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        )}
                        {status === "info" && (
                          <InfoIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        )}
                        <span className="hidden sm:inline">
                          {getStatusText(status)}
                        </span>
                      </Badge>
                      <ColoredOutlineButton
                        type="button"
                        variant="calendar"
                        size="icon"
                        className="h-7 w-7"
                        aria-label="Radera"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteNotification(notification.id);
                        }}
                      >
                        <Trash2
                          className={cn(OUTLINE_ICON_BUTTON_ICON_CLASS, "h-3.5 w-3.5")}
                          strokeWidth={2.35}
                        />
                      </ColoredOutlineButton>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {totalNotifications > 0 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalItems={totalNotifications}
            itemsPerPage={pageSize}
            onPageChange={onPageChange}
            onItemsPerPageChange={(size) => onPageSizeChange(size as NotificationPageSize)}
            itemsPerPageOptions={[...pageSizeOptions]}
          />
        )}
      </CardContent>
    </Card>
  );
};
